import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import type { Reader } from '@publiqa/shared'
import { get, post } from '../lib/api'
import { usePublic } from './context'

/**
 * Lecteur abonné connecté au site public (lien magique reçu par e-mail).
 * Sert à l'en-tête (« Mon compte ») et à recharger un article réservé après
 * connexion. Rien n'est chargé dans l'aperçu du back-office.
 */
interface ReaderCtx {
  reader: Reader | null
  /** Connu ? (null tant que la première requête n'a pas répondu.) */
  ready: boolean
  reload: () => void
  logout: () => Promise<void>
}

const Ctx = createContext<ReaderCtx>({
  reader: null,
  ready: true,
  reload: () => undefined,
  logout: async () => undefined,
})

export function ReaderProvider({ children }: { children: ReactNode }) {
  const { publication, inert } = usePublic()
  const [reader, setReader] = useState<Reader | null>(null)
  const [ready, setReady] = useState(inert)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    if (inert) return
    get<{ reader: Reader | null }>(`/public/p/${publication.slug}/reader`)
      .then((r) => setReader(r.reader))
      .catch(() => setReader(null))
      .finally(() => setReady(true))
  }, [publication.slug, inert, tick])
  const reload = useCallback(() => setTick((t) => t + 1), [])
  const logout = useCallback(async () => {
    await post(`/public/p/${publication.slug}/reader/logout`).catch(() => undefined)
    setReader(null)
  }, [publication.slug])
  return <Ctx.Provider value={{ reader, ready, reload, logout }}>{children}</Ctx.Provider>
}

export const useReader = () => useContext(Ctx)
